import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx-js-style";

// ─────────────────────────────────────────────────────────────────────────────
// ── TRAINING CURRICULUM DATABASE: STATUTORY (INDIA) & MNC CORPORATE POLICY
// ─────────────────────────────────────────────────────────────────────────────
export const TRAINING_CURRICULUM = [
  // ── 1. STATUTORY & LEGALLY REQUIRED IN INDIA ──
  {
    id: "posh",
    title: "POSH (Prevention of Sexual Harassment)",
    category: "statutory_india",
    categoryLabel: "⚖️ Legally Required in India",
    legalAct: "Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013 & Rules",
    targetAudience: "All Employees (100% Workforce) + Dedicated ICC Members Track",
    audienceKey: "all_plus_icc",
    frequency: "At joining/onboarding, then at least yearly",
    frequencyKey: "joining_annual",
    icon: "🛡️",
    badgeColor: "#dc2626",
    badgeBg: "#fef2f2",
    badgeBorder: "#fecaca",
    summary: "Companies with 10 or more employees must have an Internal Complaints Committee (ICC). The company must run regular awareness sessions for all employees, and ICC members need their own specialized training. An annual compliance report must be filed with the District Officer.",
    keyPoints: [
      "Mandatory Internal Committee (IC/ICC): Required for any establishment with 10+ employees. Headed by a senior woman employee, minimum 50% women members, and at least 1 external member from an NGO/association committed to women's causes.",
      "Dual-Track Training: (1) General sensitization for all employees at joining and annually; (2) Detailed legal, conciliation, and inquiry procedure training for ICC members.",
      "Annual Statutory Report: Mandatory submission of annual report detailing complaints received, disposed, pending, and awareness workshops conducted to the District Officer (DO) by January 31st each year.",
      "Inquiry Timeline & Standards: Strict 90-day completion timeline, principles of natural justice, interim relief provisions, zero tolerance, and strictly no monetary compensation settlement during conciliation.",
      "Strict Identity Confidentiality: Sec 16 explicitly prohibits publishing or making known the name, address, or identity of aggrieved woman, respondent, or witnesses (₹5,000 fine per breach).",
      "Statutory Penalty: Non-compliance carries a fine up to ₹50,000; repeated violation leads to double penalty and cancellation/non-renewal of business licenses/registrations."
    ],
    statutoryChecklist: [
      "Constitute ICC with Presiding Officer + 2 Employee Members + 1 External NGO Member (min 50% women)",
      "Display POSH penal consequences & ICC member contact details in physical office & intranet",
      "Include POSH policy acknowledgment in joining kit / appointment agreements",
      "Conduct at least 1 all-hands refresher workshop annually and archive attendance logs",
      "Prepare and submit Annual Report to the District Officer before 31st January"
    ]
  },
  {
    id: "fire_safety",
    title: "Workplace Safety, Fire Safety & First Aid",
    category: "statutory_india",
    categoryLabel: "⚖️ Legally Required in India",
    legalAct: "Factories Act 1948 / State Shops & Commercial Establishments Acts / National Building Code (NBC) Part 4",
    targetAudience: "All Personnel (Factory/Site workers, Warehouse staff, and Corporate Office employees)",
    audienceKey: "all",
    frequency: "At joining, then yearly or per Safety Officer's schedule",
    frequencyKey: "joining_annual",
    icon: "🧯",
    badgeColor: "#ea580c",
    badgeBg: "#fff7ed",
    badgeBorder: "#fed7aa",
    summary: "Fire drills, evacuation routes, extinguisher operation, and first-aid basics. Mandatory for factories, warehouses, and sites; strongly expected and standard for corporate offices too.",
    keyPoints: [
      "Emergency Evacuation: Clear demarcation of primary and secondary evacuation routes, illuminated emergency exits, and designated outdoor assembly points.",
      "Fire Extinguisher Proficiency: Practical training on PASS technique (Pull pin, Aim at base of fire, Squeeze lever, Sweep side-to-side) across Type A, B, C, and CO2 extinguishers.",
      "Certified First Aiders: Minimum certified first-aid trained personnel per shift with fully stocked, periodically inspected first-aid boxes (Factories Act Sec 45).",
      "Periodic Mock Drills: Bi-annual or annual evacuation drills coordinated with facility managers and local fire authorities, recording evacuation completion times.",
      "Electrical & Ergonomic Safety: Containment of electrical overloads, wiring hazards, slips/trips/falls prevention, and safe workstation ergonomics."
    ],
    statutoryChecklist: [
      "Inspect fire extinguishers, smoke alarms, and sprinkler systems monthly",
      "Conduct minimum 1 recorded building evacuation mock drill annually",
      "Maintain a verified first-aid box with a certified first-aider available on site",
      "Display evacuation floor maps and emergency contacts at every exit point"
    ]
  },
  {
    id: "dpdp",
    title: "Data Privacy (DPDP Act, 2023)",
    category: "statutory_india",
    categoryLabel: "⚖️ Legally Required in India",
    legalAct: "Digital Personal Data Protection Act, 2023 (Act No. 22 of 2023)",
    targetAudience: "Anyone handling personal data (Heightened for HR, Payroll, IT, Finance, Operations)",
    audienceKey: "data_handlers",
    frequency: "At joining, then at least yearly",
    frequencyKey: "joining_annual",
    icon: "🔒",
    badgeColor: "#0284c7",
    badgeBg: "#f0f9ff",
    badgeBorder: "#bae6fd",
    summary: "Anyone handling personal data, especially HR, payroll, and IT, must know consent requirements, purpose limitation, access rules, data minimization, and mandatory breach reporting.",
    keyPoints: [
      "Consent & Notice Architecture: Clear, itemized, unconditional notice before collecting employee/customer personal data; consent must be free, specific, informed, and capable of being withdrawn.",
      "HR & Payroll Employee Data: Restricting employee data (PAN, Aadhaar, bank details, medical records, biometrics) solely for statutory employment obligations and payroll processing.",
      "Data Principal Rights: Honoring employee/user rights to access summary of data, correct inaccuracies, erase outdated data, and access grievance redressal.",
      "Mandatory Breach Notification: Strict obligation to notify both the Data Protection Board of India (DPBI) and affected individuals within prescribed timeframes (72 hours) in case of a personal data breach.",
      "Substantial Financial Penalties: Non-compliance or failure to maintain reasonable security safeguards carries severe statutory penalties up to ₹250 Crores."
    ],
    statutoryChecklist: [
      "Issue DPDP-compliant employee consent notice during onboarding",
      "Implement role-based access control (RBAC) on payroll and HR database systems",
      "Maintain records of data processing activities and periodic access reviews",
      "Establish an incident response protocol for reporting breaches within 72 hours"
    ]
  },
  {
    id: "role_statutory",
    title: "Role-Specific Statutory Training (Machinery, Hazardous Work, PSARA & CLRA)",
    category: "statutory_india",
    categoryLabel: "⚖️ Legally Required in India",
    legalAct: "Factories Act Sec 41A-H, PSARA Act 2005, Contract Labour (R&A) Act 1970",
    targetAudience: "Specific operational roles: Machine operators, Hazardous materials handlers, Security guards, Contract workers",
    audienceKey: "role_specific",
    frequency: "Prior to deployment / machine operation, renewed semi-annually",
    frequencyKey: "prior_deployment",
    icon: "⚙️",
    badgeColor: "#d97706",
    badgeBg: "#fffbeb",
    badgeBorder: "#fde68a",
    summary: "Role-specific statutory training where applicable: machinery safety, hazardous materials handling, contract labour induction, and private security guard certification under PSARA rules.",
    keyPoints: [
      "Machinery Guarding & Lockout/Tagout (LOTO): Training on dangerous parts of machinery, emergency stop mechanisms, interlocks, and maintenance lockout procedures.",
      "Hazardous Work & PPE: Safe handling of toxic/flammable substances, Material Safety Data Sheets (MSDS), and strict enforcement of Personal Protective Equipment.",
      "Security Guard Certification (PSARA Rules): Private security personnel must undergo mandatory 100 hours of classroom instruction and 60 hours of field training (20 working days) prior to deployment.",
      "Contract Labour Welfare (CLRA Induction): Ensuring contract workers receive mandatory safety induction, statutory wage awareness, and ESI/PF registration rights."
    ],
    statutoryChecklist: [
      "Verify PSARA training certificates for all deployed security vendor personnel",
      "Conduct and log machinery safety induction before issuing factory floor pass",
      "Inspect and record daily PPE compliance in hazardous operational zones",
      "Maintain Form D register of contract labour safety briefings"
    ]
  },

  // ── 2. STANDARD FOR MNCS (COMPANY POLICY & GLOBAL GOVERNANCE) ──
  {
    id: "code_of_conduct",
    title: "Code of Conduct & Ethics",
    category: "mnc_policy",
    categoryLabel: "🌐 MNC Corporate Policy",
    legalAct: "Company Governance Standard & Service Rules",
    targetAudience: "Every Employee (100% Universal Coverage)",
    audienceKey: "all",
    frequency: "Acknowledged at joining, then reaffirmed yearly",
    frequencyKey: "joining_annual",
    icon: "📜",
    badgeColor: "#4f46e5",
    badgeBg: "#eef2ff",
    badgeBorder: "#c7d2fe",
    summary: "Core company policy ensuring business integrity, respectful workplace standards, professional boundaries, and corporate governance. Every employee acknowledges it at joining and yearly.",
    keyPoints: [
      "Universal Acknowledgment: Formal sign-off on corporate values, professional standards, and ethical boundaries at onboarding and annual appraisals.",
      "Respectful Workplace: Zero tolerance for harassment, bullying, abusive language, or discrimination in office, virtual meetings, or offsite events.",
      "Protection of Company Assets: Responsible use of corporate computing devices, IP, trade secrets, confidential customer data, and email accounts.",
      "Social Media & Public Conduct: Guidelines on brand representation, protecting company confidentiality online, and avoiding unauthorized press statements."
    ],
    statutoryChecklist: [
      "Include Code of Conduct acknowledgment form in Joining Kit",
      "Run annual digital sign-off campaign for all active personnel",
      "Document disciplinary procedure matrix for ethical infractions"
    ]
  },
  {
    id: "abac",
    title: "Anti-Bribery & Anti-Corruption (ABAC)",
    category: "mnc_policy",
    categoryLabel: "🌐 MNC Corporate Policy",
    legalAct: "Indian Prevention of Corruption Act (PCA), US FCPA, UK Bribery Act 2010",
    targetAudience: "All Employees (Mandatory for Sales, Procurement, BD, Finance, Liaisoning)",
    audienceKey: "sales_procure_finance",
    frequency: "At joining, then at least yearly",
    frequencyKey: "joining_annual",
    icon: "⚖️",
    badgeColor: "#7c3aed",
    badgeBg: "#f5f3ff",
    badgeBorder: "#ddd6fe",
    summary: "Strict prohibition of bribery, kickbacks, and improper inducements. Required by global parent companies under US FCPA, UK Bribery Act, and the Indian Prevention of Corruption Act.",
    keyPoints: [
      "Prohibition of Facilitation Payments: Zero tolerance for 'speed money' or facilitation payments to government officials or commercial counterparties.",
      "Gifts & Hospitality Ceilings: Clear monetary limits, pre-approval workflows for corporate gifting, and strict prohibition during active tender bids.",
      "Third-Party Due Diligence: Screening vendors, consultants, agents, and distributors before onboarding to prevent pass-through corrupt payments.",
      "Accurate Books & Records: Zero tolerance for off-the-books funds, fabricated invoices, or disguised expense reimbursement entries."
    ],
    statutoryChecklist: [
      "Maintain a centralized Gift & Hospitality Register with pre-approval workflow",
      "Conduct compliance due diligence on third-party liaison consultants and vendors",
      "Include explicit ABAC warranties in vendor and client contract agreements"
    ]
  },
  {
    id: "infosec",
    title: "Information Security & Cyber Hygiene Awareness",
    category: "mnc_policy",
    categoryLabel: "🌐 MNC Corporate Policy",
    legalAct: "ISO/IEC 27001 Standard / IT Act 2000 & CERT-In Directives",
    targetAudience: "All Personnel with corporate access / devices / network credentials",
    audienceKey: "all",
    frequency: "At joining, then quarterly simulated drills / annual refreshers",
    frequencyKey: "quarterly_annual",
    icon: "🛡️",
    badgeColor: "#059669",
    badgeBg: "#ecfdf5",
    badgeBorder: "#a7f3d0",
    summary: "Phishing defense, password hygiene, multi-factor authentication (MFA), device security, clean desk policy, and incident response for data protection.",
    keyPoints: [
      "Phishing & Social Engineering: Recognizing spoofed emails, malicious attachments, CEO fraud, and smishing attacks.",
      "Credential Protection: Enforcing strong passphrase guidelines, mandatory Multi-Factor Authentication (MFA), and zero password sharing.",
      "Device & Network Security: Automatic screen lock, full-disk encryption, avoiding public unencrypted Wi-Fi, and prohibition of unauthorized USB drives.",
      "Clean Desk & Clean Screen: Securing physical documents containing PII or financial data when away from workstation.",
      "Incident Reporting: Rapid protocol to notify IT Helpdesk immediately upon suspecting malware or compromised credentials."
    ],
    statutoryChecklist: [
      "Enforce mandatory MFA across all corporate email, payroll, and SaaS tools",
      "Conduct regular simulated phishing test campaigns",
      "Maintain asset custody and remote wipe protocols for corporate laptops"
    ]
  },
  {
    id: "whistleblower",
    title: "Conflict of Interest & Whistleblower Policy",
    category: "mnc_policy",
    categoryLabel: "🌐 MNC Corporate Policy",
    legalAct: "Companies Act 2013 (Sec 177 Vigil Mechanism) & SEBI LODR Regulations",
    targetAudience: "All Employees, Officers, and People Leaders",
    audienceKey: "all",
    frequency: "At joining, then reaffirmed yearly",
    frequencyKey: "joining_annual",
    icon: "📢",
    badgeColor: "#0891b2",
    badgeBg: "#ecfeff",
    badgeBorder: "#a5f3fc",
    summary: "Transparent declaration of potential conflicts of interest and a safe, confidential vigil mechanism to report wrongdoing with statutory non-retaliation protection.",
    keyPoints: [
      "Proactive Conflict Disclosure: Mandatory declaration of outside business interests, board seats, financial stakes in vendors, or hiring close relatives.",
      "Vigil Mechanism: Dedicated confidential reporting channels (email hotline, ombudsperson, anonymous reporting portal).",
      "Statutory Non-Retaliation Guarantee: Absolute protection against victimization, termination, or adverse appraisals for any bona fide whistleblower.",
      "Objective Investigation: Fair review overseen by the Audit Committee / designated Ethics Officer with documented resolution timelines."
    ],
    statutoryChecklist: [
      "Publish Whistleblower contact details on employee portal and physical notice boards",
      "Collect Annual Conflict of Interest disclosure declarations from senior staff and procurement",
      "Report quarterly summary of vigil mechanism disclosures to the Board / Audit Committee"
    ]
  },
  {
    id: "dei",
    title: "Diversity, Equity & Inclusion (DEI) & Anti-Discrimination",
    category: "mnc_policy",
    categoryLabel: "🌐 MNC Corporate Policy",
    legalAct: "Equal Remuneration Act / Rights of Persons with Disabilities Act 2016 / Transgender Persons Act 2019",
    targetAudience: "All Employees & People Managers",
    audienceKey: "all",
    frequency: "At joining, then yearly",
    frequencyKey: "joining_annual",
    icon: "🤝",
    badgeColor: "#db2777",
    badgeBg: "#fdf2f8",
    badgeBorder: "#fbcfe8",
    summary: "Fostering inclusive workplace culture, equal opportunity employment, eliminating unconscious bias, and zero tolerance for discrimination based on gender, caste, religion, or disability.",
    keyPoints: [
      "Equal Opportunity Employer: Merit-based recruitment, compensation, and career advancement regardless of gender, religion, caste, sexual orientation, or disability.",
      "Accessibility & Reasonable Accommodation: Adapting physical and digital infrastructure for Persons with Disabilities (PwD) as mandated under RPwD Act 2016.",
      "Inclusive Language & Microaggressions: Awareness of subtle non-inclusive remarks, gender-neutral communication, and cultural empathy.",
      "Equal Pay for Equal Work: Ensuring zero gender pay gaps across comparable roles and tenure levels."
    ],
    statutoryChecklist: [
      "Maintain an Equal Opportunity Policy compliant with RPwD Act 2016",
      "Conduct unconscious bias training for interviewers and hiring managers",
      "Audit gender pay parity across departmental salary bands annually"
    ]
  },
  {
    id: "insider_trading",
    title: "Insider Trading Policy (PIT Regulations)",
    category: "mnc_policy",
    categoryLabel: "🌐 MNC Corporate Policy",
    legalAct: "SEBI (Prohibition of Insider Trading) Regulations, 2015",
    targetAudience: "Designated Persons: Leadership, Finance, Strategy, Legal, Corp Comm, & Listed Parent Subsidiaries",
    audienceKey: "leadership_finance",
    frequency: "At joining, then yearly / prior to earnings announcements",
    frequencyKey: "annual_milestone",
    icon: "📈",
    badgeColor: "#b45309",
    badgeBg: "#fffbeb",
    badgeBorder: "#fde68a",
    summary: "For listed companies or subsidiaries of listed parents. Rules governing Unpublished Price Sensitive Information (UPSI), trading window closures, and pre-clearance.",
    keyPoints: [
      "Unpublished Price Sensitive Information (UPSI): Defining financial results, mergers, acquisitions, dividend decisions, or major contract wins before public disclosure.",
      "Trading Window Closures: Strict prohibition of buying or selling company securities during blackout periods (typically from quarter-end until 48 hours post results release).",
      "Pre-Clearance of Trades: Mandatory compliance officer pre-approval for trading beyond specified monetary thresholds.",
      "Chinese Walls & Need-to-Know: Preventing informal leaks of UPSI internally to colleagues or externally to friends and family."
    ],
    statutoryChecklist: [
      "Maintain a verified Digital Structured Database (SDD) of persons with access to UPSI",
      "Circulate Trading Window closure notices promptly before quarter-end",
      "Obtain annual holdings disclosure from all designated persons"
    ]
  },
  {
    id: "aml_export",
    title: "Anti-Money Laundering (AML) & Export Controls",
    category: "mnc_policy",
    categoryLabel: "🌐 MNC Corporate Policy",
    legalAct: "Prevention of Money Laundering Act (PMLA 2002) / Foreign Trade (Development & Regulation) Act",
    targetAudience: "Targeted roles: Finance, Accounts, Sales, Logistics, Trade, and Supply Chain teams",
    audienceKey: "finance_sales_logistics",
    frequency: "At joining, then at least yearly",
    frequencyKey: "joining_annual",
    icon: "🌐",
    badgeColor: "#0f766e",
    badgeBg: "#f0fdfa",
    badgeBorder: "#99f6e4",
    summary: "Preventing financial crime, Know-Your-Customer (KYC) standards, sanctions screening, and compliance with dual-use goods export control regulations for global trade.",
    keyPoints: [
      "Customer & Vendor Due Diligence (KYC): Verifying ultimate beneficial ownership (UBO) and legitimate commercial source of funds.",
      "Red Flags for Money Laundering: Unexplained round-number transactions, offshore tax haven accounts, multi-currency splitting, or cash payments.",
      "International Sanctions Screening: Checking customer and vendor entities against UN, OFAC, and Indian trade embargo sanctions lists.",
      "Export Control Classifications: SCOMET (Special Chemicals, Organisms, Materials, Equipment and Technologies) items compliance for cross-border logistics."
    ],
    statutoryChecklist: [
      "Perform KYC and beneficial ownership checks on all major institutional clients and vendors",
      "Screen cross-border transaction counterparties against international sanctions databases",
      "Implement dual-sign-off on high-value foreign currency bank transfers"
    ]
  },
  {
    id: "manager_training",
    title: "Manager & People Leader Training",
    category: "mnc_policy",
    categoryLabel: "🌐 MNC Corporate Policy",
    legalAct: "Corporate Leadership Standard & Statutory Fair Labor Practices",
    targetAudience: "All People Managers, Team Leads, Department Heads, & Interviewers",
    audienceKey: "managers",
    frequency: "Upon promotion / hiring into management, then yearly",
    frequencyKey: "promotion_annual",
    icon: "👥",
    badgeColor: "#1d4ed8",
    badgeBg: "#eff6ff",
    badgeBorder: "#bfdbfe",
    summary: "Equipping people managers to handle sensitive employee grievances, practice fair and non-discriminatory hiring, mediate conflicts, and lead constructive performance appraisals.",
    keyPoints: [
      "Grievance Resolution: Recognizing and resolving early interpersonal friction, active listening, confidentiality, and escalating to HR/ICC without bias.",
      "Fair & Legal Hiring: Structured competency-based interviews, eliminating illegal inquiries (marital status, family plans, caste, religion), and objective candidate evaluation.",
      "Constructive Performance Conversations: Delivering actionable feedback, setting objective SMART metrics, managing Performance Improvement Plans (PIPs) fairly.",
      "POSH First-Responder Role: Manager duties when an employee confides in them regarding harassment; mandatory escalation to ICC without sweeping under the rug."
    ],
    statutoryChecklist: [
      "Conduct Manager Orientation for all newly appointed team leads and managers",
      "Mandate fair interviewing refresher training before participating in hiring drives",
      "Provide guidance on standard PIP documentation and objective appraisal logs"
    ]
  }
];

// ─────────────────────────────────────────────────────────────────────────────
// ── ANNUAL TRAINING COMPLIANCE CADENCE TIMELINE
// ─────────────────────────────────────────────────────────────────────────────
export const CADENCE_MILESTONES = [
  {
    stage: "Day 1 – Week 1 (Onboarding)",
    badge: "Induction",
    color: "#2563eb",
    bg: "#eff6ff",
    items: [
      { name: "POSH Awareness Induction", mandate: "Mandatory", desc: "Basic definition, employee rights, zero tolerance, ICC contact details." },
      { name: "Code of Conduct & Ethics", mandate: "Mandatory", desc: "Digital sign-off on company core values and workplace standards." },
      { name: "Fire & Workplace Safety Orientation", mandate: "Mandatory", desc: "Evacuation route walkthrough, exit doors, and assembly location." },
      { name: "Information Security Hygiene", mandate: "Mandatory", desc: "MFA setup, corporate laptop security, password policy." }
    ]
  },
  {
    stage: "Month 1 (Role Handover)",
    badge: "Targeted",
    color: "#0891b2",
    bg: "#ecfeff",
    items: [
      { name: "Data Privacy (DPDP) for Data Handlers", mandate: "Targeted", desc: "HR, IT, Payroll, Finance training on consent and access control." },
      { name: "Role-Specific Machinery / Hazardous Safety", mandate: "Statutory", desc: "Machine guarding, PPE, chemical handling, and site safety." },
      { name: "ABAC Training for Procurement & Sales", mandate: "Targeted", desc: "Gift limits, third-party vetting, facilitation payment prohibition." }
    ]
  },
  {
    stage: "Quarterly Cadence",
    badge: "Continuous",
    color: "#059669",
    bg: "#ecfdf5",
    items: [
      { name: "Simulated Phishing Drill", mandate: "InfoSec", desc: "Simulated email campaign to measure and build phishing resilience." },
      { name: "Managerial Grievance Check-in", mandate: "People", desc: "HR-Manager touchpoints on team climate and unresolved frictions." }
    ]
  },
  {
    stage: "Semi-Annual (Every 6 Months)",
    badge: "Safety Drills",
    color: "#d97706",
    bg: "#fffbeb",
    items: [
      { name: "Live Fire & Evacuation Mock Drill", mandate: "Statutory", desc: "Timed building evacuation drill coordinated with facility warden." },
      { name: "First-Aid Equipment & Box Audit", mandate: "Statutory", desc: "Replenishing medical consumables and re-checking emergency kits." }
    ]
  },
  {
    stage: "Annual (Yearly Q4 / January Filing)",
    badge: "Statutory Audit",
    color: "#dc2626",
    bg: "#fef2f2",
    items: [
      { name: "All-Hands Annual POSH Refresher", mandate: "Statutory", desc: "Mandatory annual workshop for all personnel; log attendance." },
      { name: "POSH ICC Annual Report Submission", mandate: "Legal Deadline: Jan 31", desc: "Submit annual report to local District Officer (DO) by Jan 31st." },
      { name: "Code of Conduct & Conflict of Interest Re-certification", mandate: "Governance", desc: "Annual digital re-acknowledgment and outside interest declarations." },
      { name: "Comprehensive Cyber Security & DPDP Refresher", mandate: "IT & Privacy", desc: "Annual update on emerging threats, regulations, and access reviews." }
    ]
  }
];

// ─────────────────────────────────────────────────────────────────────────────
// ── MAIN TRAINING HUB COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function TrainingHub({ company, employees = [], currentUser, onBack, onUpdateCompanyConfig }) {
  const [activeTab, setActiveTab] = useState("curriculum"); // curriculum | calendar | logs | toolkit
  const [categoryFilter, setCategoryFilter] = useState("all"); // all | statutory_india | mnc_policy
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null); // reference details expand on request

  // Session Logging State
  const [showLogModal, setShowLogModal] = useState(false);
  const [savingSession, setSavingSession] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    training_id: "posh",
    session_title: "Annual POSH Awareness Workshop 2026",
    date: new Date().toISOString().split("T")[0],
    trainer_name: "",
    mode: "Virtual (Zoom/Teams)",
    attendees_count: 0,
    duration_hours: "1.5",
    target_group: "All Employees",
    notes: "Mandatory annual awareness session covering PoSH Act, conciliation, and ICC contact channels."
  });

  const trainingSessions = useMemo(() => {
    return company?.config?.training_sessions || [];
  }, [company?.config?.training_sessions]);

  // Filtered curriculum list
  const filteredCurriculum = useMemo(() => {
    return TRAINING_CURRICULUM.filter(item => {
      if (categoryFilter !== "all" && item.category !== categoryFilter) return false;
      if (audienceFilter !== "all" && item.audienceKey !== audienceFilter && item.audienceKey !== "all") return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = item.title.toLowerCase().includes(q);
        const matchAct = item.legalAct.toLowerCase().includes(q);
        const matchSummary = item.summary.toLowerCase().includes(q);
        const matchPoints = item.keyPoints.some(p => p.toLowerCase().includes(q));
        if (!matchTitle && !matchAct && !matchSummary && !matchPoints) return false;
      }
      return true;
    });
  }, [categoryFilter, audienceFilter, searchQuery]);

  // Save session handler
  const handleSaveSession = async (e) => {
    e.preventDefault();
    if (sessionForm.trainer_name && sessionForm.trainer_name.trim().length > 100) {
      alert("Trainer / Faculty name cannot exceed 100 characters.");
      return;
    }
    setSavingSession(true);
    try {
      const selectedModule = TRAINING_CURRICULUM.find(m => m.id === sessionForm.training_id) || {};
      const newSession = {
        id: "sess_" + Date.now(),
        training_id: sessionForm.training_id,
        training_title: selectedModule.title || sessionForm.session_title,
        category: selectedModule.category || "statutory_india",
        session_title: sessionForm.session_title,
        date: sessionForm.date,
        trainer_name: sessionForm.trainer_name ? sessionForm.trainer_name.trim().slice(0, 100) : "Internal HR / Safety Faculty",
        mode: sessionForm.mode,
        attendees_count: parseInt(sessionForm.attendees_count) || 0,
        duration_hours: sessionForm.duration_hours,
        target_group: sessionForm.target_group,
        notes: sessionForm.notes,
        created_at: new Date().toISOString(),
        created_by: currentUser?.email || "Admin"
      };

      const existingSessions = company?.config?.training_sessions || [];
      const updatedSessions = [newSession, ...existingSessions];
      const updatedConfig = {
        ...(company?.config || {}),
        training_sessions: updatedSessions
      };

      if (onUpdateCompanyConfig) {
        await onUpdateCompanyConfig(updatedConfig);
      }
      setShowLogModal(false);
      setSessionForm({
        training_id: "posh",
        session_title: "",
        date: new Date().toISOString().split("T")[0],
        trainer_name: "",
        mode: "Virtual (Zoom/Teams)",
        attendees_count: 0,
        duration_hours: "1.0",
        target_group: "All Employees",
        notes: ""
      });
      alert("✅ Training session logged successfully in corporate register!");
    } catch (err) {
      alert("Error saving training session: " + err.message);
    } finally {
      setSavingSession(false);
    }
  };

  // Delete session handler
  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("Are you sure you want to delete this recorded training session log?")) return;
    try {
      const existingSessions = company?.config?.training_sessions || [];
      const updatedSessions = existingSessions.filter(s => s.id !== sessionId);
      const updatedConfig = {
        ...(company?.config || {}),
        training_sessions: updatedSessions
      };
      if (onUpdateCompanyConfig) {
        await onUpdateCompanyConfig(updatedConfig);
      }
    } catch (err) {
      alert("Error deleting session: " + err.message);
    }
  };

  // Export Excel handler
  const handleExportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Curricula
      const currData = TRAINING_CURRICULUM.map(c => ({
        "Program Name": c.title,
        "Classification": c.category === "statutory_india" ? "Statutory Mandate (India)" : "MNC Company Policy",
        "Governing Act / Legal Standard": c.legalAct,
        "Target Audience": c.targetAudience,
        "Mandatory Frequency": c.frequency,
        "Core Objectives & Scope": c.summary
      }));
      const ws1 = XLSX.utils.json_to_sheet(currData);
      ws1["!cols"] = [{ wch: 32 }, { wch: 25 }, { wch: 40 }, { wch: 35 }, { wch: 25 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, ws1, "Compliance Curricula");

      // Sheet 2: Logged Sessions
      const sessData = (company?.config?.training_sessions || []).map(s => ({
        "Date Conducted": s.date,
        "Training Program": s.training_title,
        "Session Title": s.session_title,
        "Delivery Mode": s.mode,
        "Trainer / Faculty": s.trainer_name,
        "Attendees Count": s.attendees_count,
        "Duration (Hrs)": s.duration_hours,
        "Target Group": s.target_group,
        "Notes": s.notes,
        "Logged On": new Date(s.created_at).toLocaleDateString("en-IN")
      }));
      const ws2 = XLSX.utils.json_to_sheet(sessData.length > 0 ? sessData : [{ "Status": "No sessions recorded yet." }]);
      ws2["!cols"] = [{ wch: 15 }, { wch: 30 }, { wch: 32 }, { wch: 18 }, { wch: 25 }, { wch: 15 }, { wch: 14 }, { wch: 20 }, { wch: 40 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Training Session Logs");

      const fname = `${(company?.name || "Company").replace(/[^a-zA-Z0-9]/g, "_")}_Training_Compliance_Register.xlsx`;
      XLSX.writeFile(wb, fname);
    } catch (err) {
      alert("Failed to export Excel: " + err.message);
    }
  };

  const statutoryCount = TRAINING_CURRICULUM.filter(c => c.category === "statutory_india").length;
  const mncCount = TRAINING_CURRICULUM.filter(c => c.category === "mnc_policy").length;
  const totalAttendeesReached = trainingSessions.reduce((acc, s) => acc + (s.attendees_count || 0), 0);

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto", paddingBottom: 60 }}>
      <style>{`
        /* ── KPI Summary Cards ── */
        .th-kpi-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px 18px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          cursor: default;
        }
        .th-kpi-card:hover {
          transform: translateY(-3px);
        }
        .th-kpi-red:hover {
          border-color: #fca5a5;
          box-shadow: 0 10px 24px rgba(220, 38, 38, 0.12), 0 2px 6px rgba(0,0,0,0.04);
        }
        .th-kpi-indigo:hover {
          border-color: #c7d2fe;
          box-shadow: 0 10px 24px rgba(79, 70, 229, 0.12), 0 2px 6px rgba(0,0,0,0.04);
        }
        .th-kpi-green:hover {
          border-color: #a7f3d0;
          box-shadow: 0 10px 24px rgba(5, 150, 105, 0.12), 0 2px 6px rgba(0,0,0,0.04);
        }
        .th-kpi-amber:hover {
          border-color: #fde68a;
          box-shadow: 0 10px 24px rgba(180, 83, 9, 0.12), 0 2px 6px rgba(0,0,0,0.04);
        }

        /* ── Tab 1: Curriculum Module Cards ── */
        .th-curriculum-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .th-curriculum-card:hover {
          transform: translateY(-2px);
          border-color: #cbd5e1;
          box-shadow: 0 8px 22px rgba(0,0,0,0.07);
        }
        .th-curriculum-header {
          padding: 16px 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          transition: background 0.2s ease;
        }
        .th-curriculum-header:hover {
          background: #f8fafc;
        }
        .th-meta-card {
          background: #ffffff;
          border: 1px solid #f1f5f9;
          padding: 10px 14px;
          border-radius: 8px;
          transition: all 0.2s ease;
        }
        .th-meta-card:hover {
          background: #f8fafc;
          border-color: #e2e8f0;
          transform: translateY(-1.5px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.04);
        }
        .th-checklist-box {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          padding: 14px 18px;
          margin-bottom: 16px;
          transition: all 0.2s ease;
        }
        .th-checklist-box:hover {
          border-color: #86efac;
          box-shadow: 0 4px 14px rgba(22, 163, 74, 0.08);
          transform: translateY(-1.5px);
        }
        .th-filter-btn {
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
          border: none;
          transition: all 0.2s ease;
        }
        .th-filter-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
        }

        /* ── Tab 2: Compliance Schedule Cadence ── */
        .th-milestone-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .th-milestone-card:hover {
          transform: translateY(-2.5px);
          border-color: #cbd5e1;
          box-shadow: 0 10px 24px rgba(0,0,0,0.07);
        }
        .th-milestone-item {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 12px;
          transition: all 0.2s ease;
        }
        .th-milestone-item:hover {
          background: #ffffff;
          border-color: #94a3b8;
          transform: translateY(-2px);
          box-shadow: 0 6px 14px rgba(0,0,0,0.06);
        }

        /* ── Tab 3: Session Log & Register ── */
        .th-log-table-container {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .th-log-table-container:hover {
          border-color: #cbd5e1;
          box-shadow: 0 8px 20px rgba(0,0,0,0.06);
        }
        .th-log-row {
          border-bottom: 1px solid #f1f5f9;
          transition: background 0.18s ease;
        }
        .th-log-row:hover {
          background: #f8fafc;
        }

        /* ── Tab 4: Statutory ICC & Audit Toolkit ── */
        .th-toolkit-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 22px 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .th-toolkit-card:hover {
          transform: translateY(-2.5px);
          border-color: #cbd5e1;
          box-shadow: 0 10px 24px rgba(0,0,0,0.07);
        }
        .th-icc-role-card {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 14px;
          transition: all 0.2s ease;
        }
        .th-icc-role-card:hover {
          background: #fff5f5;
          border-color: #f87171;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(220, 38, 38, 0.1);
        }
        .th-disclosure-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 12px;
          font-size: 12.5px;
          color: #334155;
          transition: all 0.2s ease;
        }
        .th-disclosure-card:hover {
          background: #ffffff;
          border-color: #93c5fd;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(37, 99, 235, 0.1);
        }
      `}</style>

      {/* ── Top Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
        <div>
          {onBack && (
            <div
              onClick={onBack}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#2563eb", cursor: "pointer", marginBottom: 8 }}
            >
              ← Back to Dashboard
            </div>
          )}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span aria-hidden="true" style={{ fontSize: 26, lineHeight: 1.2 }}>🎓</span>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: "#0f172a" }}>Training & Compliance Hub</h1>
              <div style={{ color: "#64748b", fontSize: 13.5, marginTop: 4 }}>
                Mandatory Indian statutory training guidelines, global MNC corporate governance curricula, annual compliance schedules, and audit registers.
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={handleExportExcel}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8,
              padding: "8px 14px", fontSize: 13, fontWeight: 700, color: "#334155",
              cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
            }}
            title="Download complete Training Directory and Attendance Logs in Excel"
          >
            <span>📥</span> Export Register
          </button>
          <button
            onClick={() => {
              setSessionForm({
                training_id: "posh",
                session_title: "Annual POSH Awareness Workshop " + new Date().getFullYear(),
                date: new Date().toISOString().split("T")[0],
                trainer_name: "",
                mode: "Virtual (Zoom/Teams)",
                attendees_count: 0,
                duration_hours: "1.5",
                target_group: "All Employees",
                notes: "Mandatory annual awareness session covering PoSH Act, conciliation, and ICC contact channels."
              });
              setShowLogModal(true);
            }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#2563eb", border: "none", borderRadius: 8,
              padding: "8px 16px", fontSize: 13, fontWeight: 700, color: "#fff",
              cursor: "pointer", boxShadow: "0 2px 4px rgba(37,99,235,0.2)"
            }}
          >
            <span>＋</span> Log Training Session
          </button>
        </div>
      </div>

      {/* ── KPI Summary Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14, marginBottom: 24 }}>
        <div className="th-kpi-card th-kpi-red">
          <div style={{ fontSize: 11.5, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            🇮🇳 Statutory Mandates
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#0f172a", marginTop: 4 }}>
            {statutoryCount} Programs
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            POSH, Safety & First Aid, DPDP, PSARA
          </div>
        </div>

        <div className="th-kpi-card th-kpi-indigo">
          <div style={{ fontSize: 11.5, fontWeight: 800, color: "#4f46e5", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            🌐 MNC Governance
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#0f172a", marginTop: 4 }}>
            {mncCount} Programs
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            Ethics, ABAC, InfoSec, DEI, AML, PIT
          </div>
        </div>

        <div className="th-kpi-card th-kpi-green">
          <div style={{ fontSize: 11.5, fontWeight: 800, color: "#059669", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            📋 Completed Sessions
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#0f172a", marginTop: 4 }}>
            {trainingSessions.length} Logged
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {totalAttendeesReached} Total Attendees Recorded
          </div>
        </div>

        <div className="th-kpi-card th-kpi-amber">
          <div style={{ fontSize: 11.5, fontWeight: 800, color: "#b45309", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            🏛️ Statutory Filing Deadline
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#b45309", marginTop: 4 }}>
            31st January
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            Annual POSH ICC Report to District Officer
          </div>
        </div>
      </div>

      {/* ── Top Tabs Navigation ── */}
      <div style={{ display: "flex", gap: 8, borderBottom: "2px solid #e2e8f0", marginBottom: 20 }}>
        {[
          { id: "curriculum", label: "📚 Training Modules & Guides", count: TRAINING_CURRICULUM.length },
          { id: "calendar", label: "📅 Annual Compliance Schedule" },
          { id: "logs", label: "📋 Session Log & Attendance Register", count: trainingSessions.length },
          { id: "toolkit", label: "🏛️ Statutory ICC & Audit Toolkit" }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 18px",
              fontSize: 14,
              fontWeight: 700,
              border: "none",
              borderBottom: activeTab === tab.id ? "3px solid #2563eb" : "3px solid transparent",
              background: activeTab === tab.id ? "#eff6ff" : "transparent",
              color: activeTab === tab.id ? "#1d4ed8" : "#64748b",
              borderRadius: "8px 8px 0 0",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: -2,
              transition: "all 0.15s ease"
            }}
          >
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span style={{
                background: activeTab === tab.id ? "#2563eb" : "#e2e8f0",
                color: activeTab === tab.id ? "#fff" : "#475569",
                fontSize: 11, fontWeight: 800, padding: "2px 7px", borderRadius: 10
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ───────────────────────────────────────────────────────────────────────
          TAB 1: CURRICULUM & GUIDELINES DIRECTORY
      ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === "curriculum" && (
        <div>
          {/* Filters Bar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20, background: "#f8fafc", padding: "12px 16px", borderRadius: 10, border: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#475569" }}>Filter:</span>
              <button
                className="th-filter-btn"
                onClick={() => setCategoryFilter("all")}
                style={{
                  background: categoryFilter === "all" ? "#1e293b" : "#e2e8f0",
                  color: categoryFilter === "all" ? "#fff" : "#475569"
                }}
              >
                All Programs ({TRAINING_CURRICULUM.length})
              </button>
              <button
                className="th-filter-btn"
                onClick={() => setCategoryFilter("statutory_india")}
                style={{
                  background: categoryFilter === "statutory_india" ? "#dc2626" : "#fee2e2",
                  color: categoryFilter === "statutory_india" ? "#fff" : "#b91c1c"
                }}
              >
                🇮🇳 Legally Required in India ({statutoryCount})
              </button>
              <button
                className="th-filter-btn"
                onClick={() => setCategoryFilter("mnc_policy")}
                style={{
                  background: categoryFilter === "mnc_policy" ? "#4f46e5" : "#e0e7ff",
                  color: categoryFilter === "mnc_policy" ? "#fff" : "#3730a3"
                }}
              >
                🌐 Standard for MNCs ({mncCount})
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "1 1 240px", maxWidth: 360 }}>
              <input
                type="text"
                placeholder="Search training, act, topic, or keyword..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: "100%", padding: "8px 12px", borderRadius: 8,
                  border: "1px solid #cbd5e1", fontSize: 13, outline: "none", background: "#fff"
                }}
              />
            </div>
          </div>

          {/* Cards List */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {filteredCurriculum.length === 0 ? (
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 40, textAlign: "center", color: "#64748b" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>No training modules match your search criteria.</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Try clearing the search query or changing category filters.</div>
              </div>
            ) : (
              filteredCurriculum.map(item => {
                const isExpanded = expandedId === item.id;

                return (
                  <div
                    key={item.id}
                    className="th-curriculum-card"
                    style={{
                      border: isExpanded ? `1.5px solid ${item.badgeColor}` : undefined,
                      boxShadow: isExpanded ? "0 4px 12px rgba(0,0,0,0.06)" : undefined
                    }}
                  >
                    {/* Card Header (Clickable Accordion) */}
                    <div
                      className="th-curriculum-header"
                      onClick={() => setExpandedId(isExpanded ? null : item.id)}
                      style={{
                        background: isExpanded ? item.badgeBg : undefined
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1 }}>
                        <span style={{ fontSize: 24, lineHeight: 1 }}>{item.icon}</span>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{item.title}</span>
                            <span style={{
                              fontSize: 11, fontWeight: 800,
                              background: item.badgeBg, color: item.badgeColor,
                              border: `1px solid ${item.badgeBorder}`,
                              padding: "2px 8px", borderRadius: 12
                            }}>
                              {item.categoryLabel}
                            </span>
                          </div>
                          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 3 }}>
                            <strong style={{ color: "#334155" }}>Authority:</strong> {item.legalAct}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <div style={{ textAlign: "right", display: "none", mdDisplay: "block" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>Frequency</div>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: "#334155" }}>{item.frequency}</div>
                        </div>
                        <span style={{ fontSize: 18, color: "#64748b", fontWeight: 700 }}>
                          {isExpanded ? "▲" : "▼"}
                        </span>
                      </div>
                    </div>

                    {/* Card Expanded Content */}
                    {isExpanded && (
                      <div style={{ padding: "18px 24px", borderTop: "1px solid #e2e8f0", background: "#ffffff" }}>
                        {/* Scope Summary */}
                        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
                          <div style={{ fontSize: 11.5, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>
                            📋 Core Scope & Statutory Expectation:
                          </div>
                          <div style={{ fontSize: 13.5, color: "#1e293b", lineHeight: 1.5 }}>
                            {item.summary}
                          </div>
                        </div>

                        {/* Metadata Grid */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 18, fontSize: 13 }}>
                          <div className="th-meta-card">
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Target Audience</div>
                            <div style={{ fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{item.targetAudience}</div>
                          </div>
                          <div className="th-meta-card">
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Statutory Cadence</div>
                            <div style={{ fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{item.frequency}</div>
                          </div>
                          <div className="th-meta-card">
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Governing Standard</div>
                            <div style={{ fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{item.legalAct}</div>
                          </div>
                        </div>

                        {/* Key Curriculum & Compliance Topics */}
                        <div style={{ marginBottom: 18 }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                            <span>🎯</span> Key Learning Modules & Statutory Requirements:
                          </div>
                          <ul style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 6 }}>
                            {item.keyPoints.map((pt, idx) => (
                              <li key={idx} style={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>
                                {pt}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Statutory Checklist for HR */}
                        <div className="th-checklist-box">
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#166534", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>
                            ✅ HR & Corporate Compliance Action Checklist:
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {item.statutoryChecklist.map((chk, idx) => (
                              <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, color: "#14532d" }}>
                                <span style={{ color: "#16a34a", fontWeight: 800 }}>✓</span>
                                <span>{chk}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Action Bar */}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSessionForm({
                                training_id: item.id,
                                session_title: item.title + " Workshop " + new Date().getFullYear(),
                                date: new Date().toISOString().split("T")[0],
                                trainer_name: "",
                                mode: "Virtual (Zoom/Teams)",
                                attendees_count: 0,
                                duration_hours: "1.0",
                                target_group: item.targetAudience,
                                notes: `Completed training for ${item.title} covering ${item.legalAct}.`
                              });
                              setShowLogModal(true);
                            }}
                            style={{
                              padding: "6px 14px", fontSize: 12.5, fontWeight: 700, borderRadius: 6,
                              background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe",
                              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6
                            }}
                          >
                            <span>📝</span> Log Session for This Module
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────
          TAB 2: ANNUAL COMPLIANCE CADENCE SCHEDULE
      ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === "calendar" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ background: "#f8fafc", padding: "14px 20px", borderRadius: 10, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>📅 Annual Employee Training & Statutory Cadence Map</div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
              Recommended multi-stage timeline aligning onboarding, periodic refreshers, fire drills, and statutory POSH district filings.
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {CADENCE_MILESTONES.map((mile, idx) => (
              <div key={idx} className="th-milestone-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{
                      width: 28, height: 28, borderRadius: "50%",
                      background: mile.bg, color: mile.color, border: `1.5px solid ${mile.color}`,
                      display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13
                    }}>
                      {idx + 1}
                    </span>
                    <span style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{mile.stage}</span>
                  </div>
                  <span style={{
                    fontSize: 11.5, fontWeight: 800,
                    background: mile.bg, color: mile.color, padding: "3px 10px", borderRadius: 12
                  }}>
                    {mile.badge}
                  </span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
                  {mile.items.map((it, i) => (
                    <div key={i} className="th-milestone-item">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 13.5, color: "#1e293b" }}>{it.name}</span>
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: it.mandate.includes("Statutory") || it.mandate.includes("Deadline") ? "#dc2626" : "#2563eb" }}>
                          {it.mandate}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>
                        {it.desc}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────
          TAB 3: SESSION LOG & ATTENDANCE REGISTER
      ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === "logs" && (
        <div className="th-log-table-container">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>📋 Corporate Training Session Register</div>
              <div style={{ color: "#64748b", fontSize: 12.5, marginTop: 2 }}>
                Audit trail of completed internal and external compliance workshops, fire drills, and awareness sessions.
              </div>
            </div>
            <button
              onClick={() => setShowLogModal(true)}
              style={{
                background: "#2563eb", color: "#fff", border: "none", borderRadius: 8,
                padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer"
              }}
            >
              ＋ Record New Session
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Date</th>
                  <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Session Title & Program</th>
                  <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Delivery Mode</th>
                  <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Trainer / Faculty</th>
                  <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>Attendees</th>
                  <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Target Group</th>
                  <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {trainingSessions.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: 36, color: "#94a3b8" }}>
                      No training sessions logged yet. Click <strong>"＋ Record New Session"</strong> to log completed POSH, Fire Drill, or InfoSec sessions.
                    </td>
                  </tr>
                ) : (
                  trainingSessions.map(sess => (
                    <tr key={sess.id} className="th-log-row">
                      <td style={{ padding: "12px 14px", whiteSpace: "nowrap", color: "#0f172a", fontWeight: 600 }}>
                        {sess.date}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontWeight: 700, color: "#1e293b" }}>{sess.session_title}</div>
                        <div style={{ fontSize: 11.5, color: "#64748b" }}>{sess.training_title}</div>
                      </td>
                      <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                        <span style={{
                          background: "#eff6ff", color: "#1d4ed8", padding: "2px 8px", borderRadius: 12,
                          fontSize: 11.5, fontWeight: 700
                        }}>
                          {sess.mode}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", color: "#475569" }}>
                        {sess.trainer_name || "Internal Faculty"}
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right", fontWeight: 700, color: "#16a34a" }}>
                        {sess.attendees_count} emp
                      </td>
                      <td style={{ padding: "12px 14px", color: "#64748b" }}>
                        {sess.target_group}
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right" }}>
                        <button
                          onClick={() => handleDeleteSession(sess.id)}
                          style={{
                            background: "transparent", border: "none", color: "#dc2626",
                            cursor: "pointer", fontSize: 13, fontWeight: 700, padding: "4px 8px"
                          }}
                          title="Delete session log"
                        >
                          🗑 Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────
          TAB 4: STATUTORY ICC & COMPLIANCE TOOLKIT
      ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === "toolkit" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* POSH ICC Constitution Blueprint */}
          <div className="th-toolkit-card">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 22 }}>🏛️</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>POSH Internal Complaints Committee (ICC) Constitution Standard</div>
                <div style={{ fontSize: 12.5, color: "#64748b" }}>Statutory structure required for every company employing 10 or more personnel.</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginTop: 14 }}>
              <div className="th-icc-role-card">
                <div style={{ fontWeight: 800, fontSize: 13, color: "#991b1b", marginBottom: 4 }}>1. Presiding Officer (Chairperson)</div>
                <div style={{ fontSize: 12.5, color: "#7f1d1d", lineHeight: 1.4 }}>
                  Must be a woman employed at a senior level at the workplace from amongst the employees.
                </div>
              </div>
              <div className="th-icc-role-card">
                <div style={{ fontWeight: 800, fontSize: 13, color: "#991b1b", marginBottom: 4 }}>2. Internal Employee Members (Min 2)</div>
                <div style={{ fontSize: 12.5, color: "#7f1d1d", lineHeight: 1.4 }}>
                  Not less than two members from amongst employees committed to the cause of women or having legal knowledge/social work experience.
                </div>
              </div>
              <div className="th-icc-role-card">
                <div style={{ fontWeight: 800, fontSize: 13, color: "#991b1b", marginBottom: 4 }}>3. External Member (Independent)</div>
                <div style={{ fontSize: 12.5, color: "#7f1d1d", lineHeight: 1.4 }}>
                  One member from an NGO or association committed to the cause of women or a person familiar with sexual harassment issues.
                </div>
              </div>
              <div className="th-icc-role-card">
                <div style={{ fontWeight: 800, fontSize: 13, color: "#991b1b", marginBottom: 4 }}>4. Statutory Gender Ratio (50%+)</div>
                <div style={{ fontSize: 12.5, color: "#7f1d1d", lineHeight: 1.4 }}>
                  At least one-half (50%) of the total members so nominated on the Internal Committee must be women.
                </div>
              </div>
            </div>
          </div>

          {/* District Officer Filing Checklist */}
          <div className="th-toolkit-card">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 22 }}>📑</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>Annual POSH Report to District Officer (Due Jan 31st)</div>
                <div style={{ fontSize: 12.5, color: "#64748b" }}>Sec 21 of the PoSH Act requires preparation of an Annual Report with the following standard disclosures:</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, marginTop: 14 }}>
              {[
                "Number of complaints of sexual harassment received in the year",
                "Number of complaints disposed of during the calendar year",
                "Number of cases pending for more than 90 days",
                "Number of workshops or awareness programs against sexual harassment carried out",
                "Nature of action taken by the employer / management pursuant to ICC recommendations"
              ].map((item, idx) => (
                <div key={idx} className="th-disclosure-card">
                  <div style={{ fontWeight: 800, color: "#2563eb", marginBottom: 2 }}>Disclosure #{idx + 1}</div>
                  <div>{item}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────
          MODAL: RECORD TRAINING SESSION
      ─────────────────────────────────────────────────────────────────────── */}
      {showLogModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(15, 23, 42, 0.6)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20
        }}>
          <div style={{
            background: "#fff", borderRadius: 14, maxWidth: 560, width: "100%",
            boxShadow: "0 20px 40px rgba(0,0,0,0.2)", overflow: "hidden"
          }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
                <span>📝</span> Record Training Session
              </div>
              <button
                onClick={() => setShowLogModal(false)}
                style={{ background: "none", border: "none", fontSize: 18, color: "#64748b", cursor: "pointer", fontWeight: 700 }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveSession} style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 4 }}>
                  Training Program / Curriculum Module *
                </label>
                <select
                  value={sessionForm.training_id}
                  onChange={e => {
                    const sel = TRAINING_CURRICULUM.find(m => m.id === e.target.value);
                    setSessionForm(prev => ({
                      ...prev,
                      training_id: e.target.value,
                      session_title: sel ? `${sel.title} Workshop ${new Date().getFullYear()}` : prev.session_title
                    }));
                  }}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
                  required
                >
                  <optgroup label="⚖️ Legally Required in India">
                    {TRAINING_CURRICULUM.filter(m => m.category === "statutory_india").map(m => (
                      <option key={m.id} value={m.id}>{m.title}</option>
                    ))}
                  </optgroup>
                  <optgroup label="🌐 Standard for MNCs">
                    {TRAINING_CURRICULUM.filter(m => m.category === "mnc_policy").map(m => (
                      <option key={m.id} value={m.id}>{m.title}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 4 }}>
                  Session / Batch Title *
                </label>
                <input
                  type="text"
                  value={sessionForm.session_title}
                  onChange={e => setSessionForm(prev => ({ ...prev, session_title: e.target.value }))}
                  placeholder="e.g. Annual POSH Awareness All-Hands Q1"
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
                  required
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 4 }}>
                    Date Conducted *
                  </label>
                  <input
                    type="date"
                    value={sessionForm.date}
                    onChange={e => setSessionForm(prev => ({ ...prev, date: e.target.value }))}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 4 }}>
                    Delivery Mode *
                  </label>
                  <select
                    value={sessionForm.mode}
                    onChange={e => setSessionForm(prev => ({ ...prev, mode: e.target.value }))}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
                  >
                    <option value="Virtual (Zoom/Teams)">Virtual (Zoom / Teams)</option>
                    <option value="In-Person Classroom">In-Person Classroom</option>
                    <option value="Practical Mock Drill">Practical Mock Drill</option>
                    <option value="LMS / Digital Self-Paced">LMS / Digital Self-Paced</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 4 }}>
                    Trainer / Faculty / Agency Name
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    value={sessionForm.trainer_name}
                    onChange={e => setSessionForm(prev => ({ ...prev, trainer_name: e.target.value.slice(0, 100) }))}
                    placeholder="e.g. Adv. Priya Sharma / Internal HR"
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 4 }}>
                    Number of Attendees (Completed)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={sessionForm.attendees_count}
                    onChange={e => setSessionForm(prev => ({ ...prev, attendees_count: e.target.value }))}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13 }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 4 }}>
                  Session Notes & Completion Remarks
                </label>
                <textarea
                  value={sessionForm.notes}
                  onChange={e => setSessionForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  placeholder="Key topics covered, questions raised, attendance percentage..."
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, resize: "vertical" }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => setShowLogModal(false)}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#f8fafc", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingSession}
                  style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                >
                  {savingSession ? "Saving..." : "Save to Register"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
